import { ref, computed, defineAsyncComponent, markRaw } from "vue";
import { defineStore, acceptHMRUpdate } from "pinia";
import { useGuestProfileStore } from "@/stores/guestProfile";
import { useModalStore } from "@/stores/modal";
import { useWorkspaceLayoutStore } from "@/stores/workspaceLayout";
import { useNotificationsStore } from "@/stores/notifications";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { useApiClient } from "@/services/apiClient";

// The account writes below each handle their own failure, so a refusal
// (`require_human!`'s 403 for a signed-out tab) raises no "Permission
// denied" toast (`silent403`).
const ACCOUNT_OPTIONS = Object.freeze({ silent403: true });

// THIS STORE IS ON EVERY PAGE'S BOOT PATH (launch-marketing-pages design M2,
// the boot allowlist), so it imports no workspace store, not even
// dynamically. A workspace navigation is its caller's: My Account opens
// through `composables/accountSettingsNavigation.js#openAccountSettings`, and
// the relationship-request creates, whose toast steers to We, live in
// `composables/useRelationshipRequests.js`.
//
// The verify prompt is NOT a leaf: its code field reads the auth store and
// the door's copy. A static import would put that store graph under this
// store, and every module that imports it, and close a module cycle through
// it. It loads when the prompt is first opened, as the forward picker does in
// `weMessageActions`. A lazily loaded component is a chunk boundary, so the
// boot rule allows it.
const VerifyEmailPrompt = markRaw(
  defineAsyncComponent(
    () => import("@/components/users/subscribe-prompts/VerifyEmailPrompt.vue"),
  ),
);

// The networking-consent audiences, under the keys the whoami envelope and
// `GET|PATCH /api/v2/networking/consent` both carry them by.
const NETWORKING_CONSENT_KEYS = Object.freeze([
  "networkingAllConsent",
  "networkingFriendOfFriendsConsent",
  "networkingHubMembersConsent",
  "networkingReadersConsent",
]);

export const useUserStore = defineStore("user", () => {
  const layoutStore = useWorkspaceLayoutStore();
  const { isDesktop } = storeToRefs(layoutStore);
  const notificationsStore = useNotificationsStore();
  const currentUser = ref(null);
  const userGeographyIntents = ref([]);
  const companyMemberships = ref([]);
  const isAdmin = computed(() => !!currentUser.value?.admin);
  // The Pulse capability, read STRAIGHT off the session envelope the server
  // stamps (`Web::Session#pulse?`, true for a platform admin). Pulse surfaces
  // gate on this named capability, never on the `admin` role flag behind
  // `isAdmin`. It is stamped in BOTH directions, so an absent key is a guest
  // envelope, not a refusal to state one, and it reads false either way. The
  // Analyze roster's presence rule takes this as its `pulse` context field;
  // the reads themselves are gated server-side by `AdminGated`.
  const pulseCapability = computed(() => !!currentUser.value?.pulse);
  // The attachment-upload caps table (media-economics D10) — the SAME table
  // the server validates against (`Attachments::Caps`), stamped on the
  // signed-in envelope by `Web::Session#attachment_caps` and read defensively
  // on the guest one (`Web::GuestSession` does not yet stamp it — a backend
  // gap filed as a follow-on). ONE access path: every upload surface reads
  // this getter, never `currentUser.value.attachmentCaps` directly. Null means
  // no table reached the client — a consumer must treat that as "no
  // client-side limit" (the server still enforces), never crash or fall back
  // to a typed number of its own.
  const attachmentCaps = computed(
    () => currentUser.value?.attachmentCaps || null,
  );
  // The Tower Hunt team roster, stamped on BOTH session envelopes
  // (`Web::TeamContactPayload`) so the Contact Us menu renders identically on
  // either side of the sign-in line (artemis-human-handoff D8). Each entry is
  // `{ key, name, role, bookingUrl }` and `bookingUrl` is always present, null
  // for a contact with no booking page. Both fall back to an empty shape, so a
  // menu mounted before the session lands renders its account-free rows and
  // nothing else — never `undefined`.
  const teamContacts = computed(
    () => currentUser.value?.teamContacts || [],
  );
  const teamEmail = computed(() => currentUser.value?.teamEmail || null);
  // The author's own message-intelligence preference, read STRAIGHT off the
  // session envelope (`Web::Session#message_intel_enabled`,
  // we-message-ingestion I10). This is the ONE global reader of that fact: a
  // topical composer learns it per topic from `artemisProcessesMyMessages`, but
  // the TOPICLESS geo composer resolves no topic payload at all, so without
  // this getter it could not tell an opted-out author that Artemis skips their
  // messages.
  //
  // THREE-VALUED, like the per-topic field it complements: true / false /
  // null, where null means no envelope resolved the fact (a guest, or the
  // session has not landed yet) and is NEVER read as an opt-out. `??` rather
  // than `||` for exactly that reason.
  //
  // WRITABLE, because the AI-preferences pane edits the same fact through a
  // different endpoint. It commits here only once the server has accepted the
  // flip, so every composer follows without a reload and the pane's optimistic
  // switch can never publish a privacy promise the server refused.
  const messageIntelEnabled = computed({
    get() {
      return currentUser.value?.messageIntelEnabled ?? null;
    },
    set(newValue) {
      if (!currentUser.value) return;
      currentUser.value.messageIntelEnabled = newValue;
    },
  });
  const signedIn = computed(() => !!currentUser.value?.signedIn);
  // Canonical authoritative source for the current user's database id.
  // Returns an integer when authenticated, null for unauthenticated/guest.
  // Consumed by `useArtemisClientState` (ephemeral.current_user_id) — useUserStore
  // is canonical (authoritative) over useAuthStore, which only orchestrates
  // sign-in/out and delegates currentUser storage back to this store.
  const currentUserId = computed(() => {
    if (!signedIn.value) return null;
    const rawId = currentUser.value?.id;
    return Number.isInteger(rawId) ? rawId : null;
  });
  const emailUnverified = computed(
    () =>
      signedIn.value &&
      currentUser.value?.emailVerificationState === "unverified",
  );
  const emailVerified = computed(
    () =>
      signedIn.value &&
      currentUser.value?.emailVerificationState === "verified",
  );
  const firstName = computed(() => currentUser.value?.firstName);
  const availableBalance = computed({
    get() {
      return currentUser.value?.availableBalance;
    },
    set(newBalance) {
      currentUser.value.availableBalance = newBalance;
    },
  });
  const presence = computed({
    get() {
      return currentUser.value?.presence;
    },
    set(newPresence) {
      currentUser.value.presence = newPresence;
    },
  });
  const reputation = computed({
    get() {
      return currentUser.value?.reputation;
    },
    set(newReputation) {
      currentUser.value.reputation = newReputation;
    },
  });
  const reputable = computed({
    get() {
      return currentUser.value?.reputable;
    },
    set(newReputable) {
      currentUser.value.reputable = newReputable;
    },
  });
  const correctPercentage = computed({
    get() {
      return currentUser.value?.correctPercentage;
    },
    set(newCorrectPercentage) {
      currentUser.value.correctPercentage = newCorrectPercentage;
    },
  });
  const validationSkips = computed({
    get() {
      return currentUser.value?.validationSkips;
    },
    set(newSkips) {
      currentUser.value.validationSkips = newSkips;
    },
  });
  /**
   * The skip balance, as the server last stated it (validation-quiet-queue D6).
   *
   * THE SESSION BOOTSTRAP IS THE INITIAL VALUE AND NOTHING MORE. Every later
   * truth arrives on a response that already had to compute the balance to
   * answer at all — the vote create, the queue index and the single-group read
   * each carry `validation_skips` — so this ONE setter, called from each of
   * them, is why the header chip and the pane's server-stamped `skip`
   * capability agree without a refetch. It is deliberately not a client-side
   * gate on the control: the server refuses a skip at zero, and a store that
   * decided for itself would offer a gesture the endpoint would reject.
   *
   * A RESPONSE WITHOUT THE KEY SAYS NOTHING ABOUT THE BALANCE, so an absent
   * value leaves the held one alone rather than blanking the chip. The
   * admin-settle 201 and the funding-shortfall arm both answer without a
   * balance — neither spent a skip — and reading their silence as zero would
   * withhold the control from a validator who still has skips.
   *
   * @param {number|string|null|undefined} newSkips the response's balance
   */
  function setValidationSkips(newSkips) {
    if (newSkips == null || currentUser.value == null) return;

    const balance = Number(newSkips);
    if (!Number.isFinite(balance)) return;

    validationSkips.value = balance;
  }
  const sessionGeographyIntents = computed({
    get() {
      return currentUser.value?.sessionGeographyIntents;
    },
    set(newCount) {
      currentUser.value.sessionGeographyIntents = newCount;
    },
  });
  const linked = computed({
    get() {
      return currentUser.value?.linked;
    },
    set(bool) {
      currentUser.value.linked = bool;
    },
  });
  const linkRequestPending = computed({
    get() {
      return currentUser.value?.linkRequestPending;
    },
    set(bool) {
      currentUser.value.linkRequestPending = bool;
    },
  });
  const linkedContactId = computed(
    () => currentUser.value?.linkedContactId || null,
  );
  // Plain read-only computed so `storeToRefs` consumers receive a ref that
  // tracks the underlying `currentUser` mutation reliably on initial render.
  // Writes go through `persistDefaultGeographyMode` exposed below — components
  // wire a local writable computed (read this ref, write via the action) so
  // hydration after mount propagates the initial getter value.
  const defaultGeographyMode = computed(() =>
    currentUser.value?.defaultGeographyMode,
  );
  // Plain read-only computed mirroring `defaultGeographyMode` above.
  // Writes go through `persistMapPresenceVisible` below — the
  // AccountSettings "Show me on the map" toggle component drives both
  // the persistence call and the immediate `mapPresence.writeViewport`
  // on the `false → true` transition (D7 of actor-map-presence-v2).
  // THREE-VALUED: `true` / `false` as the whoami envelope stated them, `null`
  // before any envelope has (no session yet, or a guest) — never `undefined`,
  // and never a default the server did not send.
  const mapPresenceVisible = computed(
    () => currentUser.value?.mapPresenceVisible ?? null,
  );
  // The four networking-consent audiences: who may ask to connect with this
  // member. One setter shape for all four, each persisting through the one v2
  // endpoint (element-inspector-redesign D11).
  const networkingAllConsent = networkingConsentAudience("networkingAllConsent");
  const networkingFriendOfFriendsConsent = networkingConsentAudience(
    "networkingFriendOfFriendsConsent",
  );
  const networkingHubMembersConsent = networkingConsentAudience(
    "networkingHubMembersConsent",
  );
  const networkingReadersConsent = networkingConsentAudience(
    "networkingReadersConsent",
  );

  function networkingConsentAudience(key) {
    return computed({
      get() {
        return currentUser.value?.[key];
      },
      set(bool) {
        if (currentUser.value?.[key] === bool) {
          return;
        }
        persistNetworkingConsent({ [key]: bool });
      },
    });
  }

  // th-24pq0 — Server-issued, one-shot, dismissable login notices
  // (e.g. `spending_cap_recalibration_v1`).  The whoami payload carries
  // an `userNotices` array of `{ id, kind, createdAt }`; banner
  // components read this ref and call `dismissUserNotice(id)` to
  // mark a notice retired.  Optimistic local removal + PATCH to
  // `/api/v2/user_notices/:id/dismiss`; on transport failure we
  // restore the previous list (so the banner re-renders) and let the
  // next whoami reconcile.
  const userNotices = computed(
    () => currentUser.value?.userNotices || [],
  );

  function hasUserNotice(kind) {
    return userNotices.value.some((n) => n.kind === kind);
  }

  // The sign-up gift's dollar label (platform-budgets D10), projected
  // server-side (`Web::Session#signup_gift_usd_label`) — the amount the
  // `signup_gift_granted` notice names. Null for a user never enrolled.
  const signupGiftUsdLabel = computed(
    () => currentUser.value?.signupGiftUsdLabel || null,
  );

  async function dismissUserNotice(id) {
    if (!currentUser.value || !Array.isArray(currentUser.value.userNotices)) {
      return;
    }
    const previous = currentUser.value.userNotices.slice();
    currentUser.value.userNotices = previous.filter((n) => n.id !== id);
    try {
      await useApiClient().patch(`/user_notices/${id}/dismiss`, undefined, ACCOUNT_OPTIONS);
    } catch (_err) {
      currentUser.value.userNotices = previous;
    }
  }

  const linkable = computed(() => !linked.value && !linkRequestPending.value);
  const availableCollectibleCardCount = ref(0);

  const guestProfile = useGuestProfileStore();
  const { geographyIntents, discoverySearches, completedCardIds } =
    storeToRefs(guestProfile);
  function fetchAvailableCollectibleCardCount() {
    return new Promise((resolve) => {
      const payload = {
        cardIds: signedIn.value ? [] : completedCardIds.value,
        geographyIntents: signedIn.value ? [] : geographyIntents.value,
        discoverySearches: signedIn.value ? [] : discoverySearches.value,
        mobile: !isDesktop.value,
      };
      useApiClient()
        .post("/my_collectible_cards_count", payload, ACCOUNT_OPTIONS)
        .then((count) => {
          availableCollectibleCardCount.value = count;
          resolve();
        });
    });
  }
  async function persistDefaultGeographyMode(value) {
    const previousValue = currentUser.value?.defaultGeographyMode;

    try {
      const mode = await useApiClient().patch(
        "/default_geography_modes",
        { default_geography_mode: value },
        ACCOUNT_OPTIONS,
      );

      if (
        (mode === "current_view" || mode === "explored_areas") &&
        currentUser.value
      ) {
        currentUser.value.defaultGeographyMode = mode;
        notificationsStore.addNotification("defaultGeographyModeUpdated");
      } else if (currentUser.value) {
        currentUser.value.defaultGeographyMode = previousValue;
      }
    } catch (error) {
      if (currentUser.value) {
        currentUser.value.defaultGeographyMode = previousValue;
      }
      notificationsStore.addNotification("defaultGeographyModeUpdateFailed");
    }
  }

  /**
   * Persist the actor-map-presence opt-out flag to
   * `PATCH /api/v2/map_presence_visibilities`.
   *
   * Returns a boolean reflecting the persisted value (matches the JSON
   * the controller renders — `current_user.map_presence_visible` after
   * reload). On unexpected response shape or transport error the prior
   * value is restored locally and `null` is returned so callers can
   * skip the side-effects that should fire only after a successful
   * `false → true` transition (the immediate `writeViewport` repopulate
   * lives in the AccountSettings component handler — design.md D7).
   */
  async function persistMapPresenceVisible(bool) {
    const previousValue = currentUser.value?.mapPresenceVisible;

    try {
      const visible = await useApiClient().patch(
        "/map_presence_visibilities",
        { map_presence_visible: bool },
        ACCOUNT_OPTIONS,
      );

      if ((visible === true || visible === false) && currentUser.value) {
        currentUser.value.mapPresenceVisible = visible;
        notificationsStore.addNotification("mapPresenceVisibleUpdated");
        return visible;
      } else if (currentUser.value) {
        currentUser.value.mapPresenceVisible = previousValue;
        return null;
      }
    } catch (_error) {
      if (currentUser.value) {
        currentUser.value.mapPresenceVisible = previousValue;
      }
      notificationsStore.addNotification("mapPresenceVisibleUpdateFailed");
      return null;
    }

    return null;
  }

  /**
   * Persist one networking-consent audience through
   * `PATCH /api/v2/networking/consent`.
   *
   * The endpoint answers with EVERY audience, under the keys `Web::Session`
   * bootstraps them with, so all four are written back from what the server
   * stored. A refusal leaves the held session as it was and says so; a 403 is
   * already toasted by `baseFetch`.
   *
   * @param {Object<string, boolean>} consent one audience key and its value
   */
  async function persistNetworkingConsent(consent) {
    try {
      const response = await useApiClient().patch(
        `/networking/consent`,
        consent,
      );
      const answered = response?.data;
      const stored = {};
      if (answered != null) {
        for (const key of NETWORKING_CONSENT_KEYS) {
          if (key in Object(answered)) stored[key] = answered[key];
        }
      }

      if (Object.keys(stored).length === 0 || !currentUser.value) return;

      Object.assign(currentUser.value, stored);
      notificationsStore.addNotification("networkingConsentUpdated");
    } catch (error) {
      if (error?.status === 403) return;

      notificationsStore.addDataProcessingNotification(
        "error",
        "Networking preference not saved",
        typeof error?.details?.error === "string" && error.details.error
          ? error.details.error
          : "We couldn't save that change. Please try again.",
        8,
      );
    }
  }

  const emailRegex = RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );

  const router = useRouter();
  const route = useRoute();

  const modalStore = useModalStore();
  const { modalPayload } = storeToRefs(modalStore);

  // The verify prompt, raised for an unverified address by the actions that
  // need a verified one: this store's company-membership request, and the
  // relationship-request creates (`composables/useRelationshipRequests.js`).
  function promptToVerify() {
    modalPayload.value = {
      size: "base",
      theme: "light",
      component: VerifyEmailPrompt,
      props: {},
    };
  }

  async function fetchCompanyMemberships() {
    const v2Api = useApiClient();
    const response = await v2Api.get(`/company_memberships`);

    if (response?.data?.companyMemberships) {
      companyMemberships.value = response.data.companyMemberships;
    }
  }

  function companyMemberExistsFor(companyId) {
    return companyMemberships.value.some((membership) => {
      return membership.companyId == companyId;
    });
  }

  async function requestCompanyMembership(companyId) {
    if (emailUnverified.value) {
      promptToVerify();
    } else if (!companyMemberExistsFor(companyId)) {
      const v2Api = useApiClient();
      const response = await v2Api.post(`/company_memberships`, {
        companyId,
      });

      if (response?.data?.companyMembership) {
        // The new membership first, then the held ones, one per company.
        const companies = new Set();
        companyMemberships.value = [
          response.data.companyMembership,
          ...companyMemberships.value,
        ].filter((membership) => {
          const companyId = membership?.companyId;
          if (companies.has(companyId)) return false;
          companies.add(companyId);
          return true;
        });
        notificationsStore.addNotification("companyMembershipRequestSubmitted");
      }
    }
  }

  return {
    currentUser,
    firstName,
    isAdmin,
    pulseCapability,
    attachmentCaps,
    teamContacts,
    teamEmail,
    messageIntelEnabled,
    signedIn,
    currentUserId,
    userGeographyIntents,
    companyMemberships,
    emailUnverified,
    emailVerified,
    availableBalance,
    presence,
    reputation,
    reputable,
    linked,
    linkRequestPending,
    linkedContactId,
    linkable,
    defaultGeographyMode,
    persistDefaultGeographyMode,
    mapPresenceVisible,
    persistMapPresenceVisible,
    networkingAllConsent,
    networkingFriendOfFriendsConsent,
    networkingHubMembersConsent,
    networkingReadersConsent,
    userNotices,
    hasUserNotice,
    dismissUserNotice,
    signupGiftUsdLabel,
    correctPercentage,
    validationSkips,
    setValidationSkips,
    sessionGeographyIntents,
    availableCollectibleCardCount,
    fetchAvailableCollectibleCardCount,
    promptToVerify,
    requestCompanyMembership,
    fetchCompanyMemberships,
    companyMemberExistsFor,
    emailRegex,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
}
